import MealCategory from './meal-category';
import MealItem from './meal-item';
import MealItemAssignmentIngredient from './meal-item-assignment-ingredient';
import MealItemIngredient from './meal-item-ingredient';
import Measure from './measure';
import {
  BaseEntity,
  JsonApiProperty,
  JsonApiRelationship,
  JsonApiResource,
} from '../jsonapi-provider';
import { type ServeType } from '../types';
import type Ingredient from './ingredient';

@JsonApiResource({
  type: 'meal-item-assignment',
})
export default class MealItemAssignment extends BaseEntity {
  @JsonApiProperty({ resolver: Number }) public sortOrder: number = 127;
  @JsonApiProperty({ resolver: (value) => (value ? Number(value) : value) }) servingSize:
    | MealItem['servingSize']
    | null = null;

  @JsonApiProperty({ resolver: (value) => (value ? Number(value) : value) }) size:
    | MealItem['size']
    | null = null;

  @JsonApiRelationship('meal-category') mealCategory = new MealCategory();
  @JsonApiRelationship('meal-item') baseMealItem = new MealItem();

  @JsonApiRelationship('measure') servingMeasure: MealItem['servingMeasure'] | null = new Measure();
  @JsonApiRelationship('measure') measure: MealItem['measure'] | null = new Measure();
  @JsonApiRelationship('serving-tool') servingTool: MealItem['servingTool'] | null = null;
  @JsonApiRelationship('meal-item-assignment-ingredient')
  mealItemAssignmentIngredients: MealItemAssignmentIngredient[] = [];

  @JsonApiProperty({ type: 'object', readOnly: true })
  public getEffective(): MealItemAssignment {
    const ingredients: Record<
      Exclude<Ingredient['id'], undefined>,
      MealItemAssignmentIngredient
    > = {};
    this.baseMealItem.mealItemIngredients.forEach((mii) => {
      ingredients[mii.ingredient.id!] = new MealItemIngredient(
        undefined,
        mii.ingredient,
        mii.units,
        mii.measure,
        mii.mealItem,
        mii.sortOrder
      ).toMealItemAssignmentIngredient(new MealItemAssignment(this.id));
    });
    this.mealItemAssignmentIngredients.forEach((miai) => {
      if (ingredients[miai.ingredient.id!]) {
        Object.assign(ingredients[miai.ingredient.id!], {
          id: miai.id,
          units: miai.units,
          originalUnits: ingredients[miai.ingredient.id!].units,
          // measure: miai.measure,
        });
      } else {
        ingredients[miai.ingredient.id!] = new MealItemAssignmentIngredient(
          miai.id,
          miai.ingredient,
          miai.units,
          miai.measure,
          undefined,
          miai.originalUnits ?? 0,
          miai.sortOrder
        );
      }
    });

    return new MealItemAssignment(
      this.id,
      this.mealCategory,
      this.baseMealItem,
      this.serveType,
      this.sortOrder,
      this.servingSize ?? this.baseMealItem.servingSize,
      this.servingMeasure ?? this.baseMealItem.servingMeasure,
      this.size ?? this.baseMealItem.size,
      this.measure ?? this.baseMealItem.measure,
      this.servingTool ?? this.baseMealItem.servingTool,
      Object.values(ingredients).toSorted((a, b) => a.sortOrder - b.sortOrder)
    );
  }

  @JsonApiProperty({ type: 'object', readOnly: true })
  public getActual(): MealItemAssignment {
    return new MealItemAssignment(
      this.id,
      this.mealCategory,
      new MealItem(this.baseMealItem.id),
      this.serveType,
      this.sortOrder,
      (this.servingSize == null || this.servingSize === this.baseMealItem.servingSize) &&
      (this.servingMeasure == null ||
        this.servingMeasure.id === this.baseMealItem.servingMeasure.id)
        ? null
        : this.servingSize,
      (this.servingSize == null || this.servingSize === this.baseMealItem.servingSize) &&
      (this.servingMeasure == null ||
        this.servingMeasure.id === this.baseMealItem.servingMeasure.id)
        ? null
        : this.servingMeasure,
      (this.size == null || this.size === this.baseMealItem.size) &&
      (this.measure == null || this.measure.id === this.baseMealItem.measure.id)
        ? null
        : this.size,
      (this.size == null || this.size === this.baseMealItem.size) &&
      (this.measure == null || this.measure.id === this.baseMealItem.measure.id)
        ? null
        : this.measure,
      this.servingTool?.id == null || this.servingTool.id === this.baseMealItem.servingTool?.id
        ? null
        : this.servingTool,
      this.mealItemAssignmentIngredients.filter(
        (miai) => miai.originalUnits == null || miai.units !== miai.originalUnits
      )
    );
  }

  @JsonApiProperty({ type: 'object', readOnly: true })
  public get isPrimary(): boolean {
    return this.sortOrder === 0;
  }

  constructor(
    id?: MealItemAssignment['id'],
    mealCategory: MealItemAssignment['mealCategory'] = new MealCategory(),
    baseMealItem: MealItemAssignment['baseMealItem'] = new MealItem(),
    public serveType: ServeType = 'INDIVIDUAL',
    sortOrder: number = 32000,
    servingSize: MealItemAssignment['servingSize'] = null,
    servingMeasure: MealItemAssignment['servingMeasure'] = null,
    size: MealItemAssignment['size'] = null,
    measure: MealItemAssignment['measure'] = null,
    servingTool: MealItemAssignment['servingTool'] = null,
    mealItemAssignmentIngredients: MealItemAssignment['mealItemAssignmentIngredients'] = []
  ) {
    super();
    this.id = id;
    this.mealCategory = mealCategory;
    this.sortOrder = sortOrder;
    this.baseMealItem = baseMealItem;
    this.servingMeasure = servingMeasure;
    this.measure = measure;
    this.servingTool = servingTool;
    this.mealItemAssignmentIngredients = mealItemAssignmentIngredients;
    this.servingSize = servingSize;
    this.size = size;
  }
}
